import type { WaterProfile, HardnessCategory, ArsenicRisk } from "./types";
import type { Answers } from "@/schema/quizSchema";
import type { WaterView } from "./view";

export type Severity = "leve" | "moderado" | "alto";
export type MaterialKey = "carbon" | "kdf" | "sulfito" | "vitc" | "zeolita" | "mecanica";

export interface ProfileCopy {
  legal: boolean;
  piel?: string; // omitted for the legal profile (C)
  pelo?: string;
  solTitle: string;
  bridge: string;
  inside: string; // "Lleva: {inside}"
  materials: MaterialKey[];
}

/** Per-profile copy — verbatim from the approved mockup PROFILES object. */
export const PROFILES: Record<WaterProfile, ProfileCopy> = {
  A: {
    legal: false,
    piel: "**Reseca** y te deja tirantez al salir.",
    pelo: "**Apaga el brillo** y dispara el frizz.",
    solTitle: "Cortale el cloro a tu ducha.",
    bridge:
      "Tu crema trabaja por fuera, después de la ducha. **El cloro ya entró.** AURAL lo filtra en el agua, antes de que toque tu piel.",
    inside: "Carbón de coco + KDF-55 + sulfito + vitamina C + filtración mecánica.",
    materials: ["carbon", "kdf", "sulfito", "vitc", "mecanica"],
  },
  B: {
    legal: false,
    piel: "**Forma una capa** que tu crema no atraviesa.",
    pelo: "**Áspero y con frizz** por el calcio.",
    solTitle: "Cambiá el agua que toca tu piel.",
    bridge:
      "Hidratar no saca la capa de calcio — la tapa. **AURAL trabaja en el agua**, filtrando el calcio y el cloro antes de que lleguen a tu piel y tu pelo.",
    inside: "Maifan + zeolita + KDF-55 + carbón de coco + sulfito.",
    materials: ["zeolita", "kdf", "carbon", "sulfito"],
  },
  C: {
    legal: true,
    solTitle: "AURAL cuida lo que toca tu piel.",
    bridge:
      "Para el arsénico, lo correcto es un filtro de **consumo NSF/ANSI 53** — no la ducha. Lo que AURAL sí hace: filtrar el **cloro y la dureza** que tocan tu piel cada día.",
    inside: "Carbón de coco + KDF-55 + sulfito + vitamina C + filtración mecánica.",
    materials: ["carbon", "kdf", "sulfito", "vitc"],
  },
  D: {
    legal: false,
    piel: "**Resequedad** en duchas calientes largas.",
    pelo: "**Perdió suavidad** sin razón aparente.",
    solTitle: "Sumá la última pieza.",
    bridge:
      "Tu agua ya nace excelente. **Lo único que falta** es filtrar el cloro residual obligatorio antes de que toque tu piel.",
    inside: "Carbón de coco + KDF-55 + sulfito + vitamina C + filtración mecánica.",
    materials: ["carbon", "kdf", "sulfito", "vitc", "mecanica"],
  },
  F: {
    legal: false,
    piel: "**Resequedad o tirantez** después de la ducha.",
    pelo: "**Perdió brillo** últimamente.",
    solTitle: "Empezá por el factor común.",
    bridge:
      "El cloro residual está en toda red, sin importar tu zona exacta. **AURAL lo filtra** antes de que toque tu piel.",
    inside: "Carbón de coco + KDF-55 + sulfito + vitamina C + Maifan.",
    materials: ["carbon", "kdf", "sulfito", "vitc"],
  },
};

export const MAT_LABEL: Record<MaterialKey, string> = {
  carbon: "Carbón de coco",
  kdf: "KDF-55",
  sulfito: "Sulfito de calcio",
  vitc: "Vitamina C",
  zeolita: "Zeolita",
  mecanica: "Filtración mecánica",
};

export const MAT_DESC: Record<MaterialKey, string> = {
  carbon: "Atrapa cloro, olores y trihalometanos.",
  kdf: "Neutraliza cloro y captura metales pesados.",
  sulfito: "Neutraliza cloro y cloraminas, también en caliente.",
  vitc: "Neutraliza cloraminas; antioxidante en la piel.",
  zeolita: "Captura metales disueltos y reduce la dureza.",
  mecanica: "Retiene óxido y sedimentos a 5 micras.",
};

/** Same-origin filenames under /aural/ (see assets.ts). Maifan has no image -> never shown. */
export const MAT_IMAGE: Record<MaterialKey, string> = {
  carbon: "carbon-coco.webp",
  kdf: "kdf55.png",
  sulfito: "sulfito-calcio.webp",
  vitc: "vitamina-c.webp",
  zeolita: "zeolita.webp",
  mecanica: "pp-cotton.webp",
};
export const PRODUCT_IMAGE = "aural-product.webp";

// ---- number formatting (es-AR) ----
export function nf(n: number, decimals = 0): string {
  return Number(n).toLocaleString("es-AR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// ---- risk pills ----
export type PillLevel = "ok" | "warn" | "bad" | "none";
const PILL: Record<PillLevel, string> = { ok: "Bajo", warn: "Medio", bad: "Alto", none: "s/d" };

export function hardnessLevel(cat: HardnessCategory): PillLevel {
  if (cat === "muy blanda" || cat === "blanda") return "ok";
  if (cat === "moderada") return "warn";
  if (cat === "dura" || cat === "muy dura") return "bad";
  return "none";
}
export function chlorineLevel(max: number | null): PillLevel {
  if (max == null) return "warn";
  if (max <= 0.4) return "ok";
  if (max <= 0.7) return "warn";
  return "bad";
}
export function arsenicLevel(risk: ArsenicRisk): PillLevel {
  return risk === "bajo" ? "ok" : risk === "medio" ? "warn" : risk === "alto" ? "bad" : "none";
}
export const hardnessPill = (c: HardnessCategory) => PILL[hardnessLevel(c)];
export const chlorinePill = (m: number | null) => PILL[chlorineLevel(m)];
export const arsenicPill = (r: ArsenicRisk) => PILL[arsenicLevel(r)];
export const pillText = (l: PillLevel) => PILL[l];

// ---- stat-strip display values ----
export interface StatChip {
  label: string;
  value: string;
  unit: string;
  level: PillLevel;
  pill: string;
}
export function statValues(view: WaterView): StatChip[] {
  const chlorineVal =
    view.chlorineMin != null && view.chlorineMax != null
      ? `${nf(view.chlorineMin, 1)}–${nf(view.chlorineMax, 1)}`
      : "0,2–1,0";
  let arsenicVal: string;
  if (view.arsenicUgL != null) arsenicVal = `~${nf(view.arsenicUgL)}`;
  else
    arsenicVal =
      view.arsenicRisk === "bajo"
        ? "<10"
        : view.arsenicRisk === "medio"
          ? "10–50"
          : view.arsenicRisk === "alto"
            ? ">50"
            : "—";
  return [
    {
      label: "Dureza",
      value: view.hardnessMgL != null ? nf(view.hardnessMgL) : "—",
      unit: "mg/L",
      level: hardnessLevel(view.hardnessCat),
      pill: hardnessPill(view.hardnessCat),
    },
    {
      label: "Cloro",
      value: chlorineVal,
      unit: "mg/L",
      level: chlorineLevel(view.chlorineMax),
      pill: chlorinePill(view.chlorineMax),
    },
    {
      label: "Arsénico",
      value: arsenicVal,
      unit: view.arsenicUgL != null ? "µg/L" : " ",
      level: arsenicLevel(view.arsenicRisk),
      pill: arsenicPill(view.arsenicRisk),
    },
  ];
}

// ---- severity meter ----
export const SEV_POS: Record<Severity, string> = { leve: "16%", moderado: "52%", alto: "88%" };
export const SEV_LABEL: Record<Severity, string> = { leve: "Leve", moderado: "Moderado", alto: "Alto" };
export const SEV_CLAUSE: Record<Severity, string> = {
  leve: "todavía leve, pero el agua sigue actuando.",
  moderado: "y ya aparecen las primeras señales.",
  alto: "y tu piel ya muestra señales claras.",
};

// ---- eyebrows ----
export const eyebrow1 = (profile: WaterProfile) => (profile === "C" ? "Lo que importa" : "Tu diagnóstico");
export const eyebrow2 = (profile: WaterProfile) =>
  profile === "C" ? "Lo que sí podemos hacer" : "Tu solución";

// ---- page-1 punch (verbatim mockup logic) ----
export function buildPunch(profile: WaterProfile, view: WaterView, severity: Severity): string {
  if (profile === "C") {
    const a = view.arsenicUgL != null ? nf(view.arsenicUgL) : "—";
    return `Tu zona tiene arsénico (~##${a}## µg/L) en el agua de red. Es un tema de lo que **bebés**, no de tu ducha — te lo explicamos sin vueltas.`;
  }
  const clause = SEV_CLAUSE[severity];
  if (profile === "B" && view.hasLocalityData && view.hardnessMgL != null) {
    return `Tu agua tiene ##${nf(view.hardnessMgL)}## mg/L de calcio, ${clause}`;
  }
  if (view.hasLocalityData && view.chlorineMin != null && view.chlorineMax != null) {
    return `Tu agua trae cloro residual (##${nf(view.chlorineMin, 1)}–${nf(view.chlorineMax, 1)}## mg/L), ${clause}`;
  }
  return `El cloro de red (##0,2–1,0## mg/L) toca tu piel cada ducha, ${clause}`;
}

// ---- the two causes (verbatim mockup logic, operating on WaterView) ----
export interface Cause {
  label: string;
  explain: string;
}
export function causesFor(profile: WaterProfile, view: WaterView, answers: Answers): [Cause, Cause] {
  const cl =
    view.chlorineMin != null && view.chlorineMax != null
      ? `${nf(view.chlorineMin, 1)}–${nf(view.chlorineMax, 1)} mg/L`
      : "0,2–1,0 mg/L";
  let c1: Cause;
  if (profile === "B" && view.hasLocalityData && view.hardnessMgL != null) {
    c1 = { label: `Dureza alta: ${nf(view.hardnessMgL)} mg/L`, explain: "El calcio forma una capa mineral en cada ducha." };
  } else if (view.hasLocalityData) {
    c1 = { label: `Cloro de red: ${cl}`, explain: "Entra con la ducha caliente y reseca tu piel." };
  } else {
    c1 = { label: `Cloro de red: ${cl}`, explain: "El factor común en toda el agua argentina." };
  }

  const s = Array.isArray(answers.q_sintomas) ? (answers.q_sintomas as string[]) : [];
  let c2: Cause;
  if (answers.q_color === "si" || s.includes("color")) {
    c2 = { label: "Tu color teñido se va", explain: "El cloro y la cal lo desvanecen antes de tiempo." };
  } else if (s.includes("caspa")) {
    c2 = { label: "Caspa y cuero cabelludo seco", explain: "El cloro reseca el cuero cabelludo en duchas largas." };
  } else if (s.includes("picazon")) {
    c2 = { label: "La picazón que sentís", explain: "El cloro irrita la piel sensible apenas salís." };
  } else if (s.includes("frizz")) {
    c2 = { label: "El frizz que notás", explain: "El agua dura levanta la cutícula del pelo." };
  } else if (profile === "B") {
    c2 = { label: `Cloro de red: ${cl}`, explain: "Suma resequedad sobre la capa de calcio." };
  } else {
    c2 = { label: "Metales de la red", explain: "Se acumulan en el pelo y le quitan brillo." };
  }
  return [c1, c2];
}

// ---- page-1 / page-2 CTA + advisory + static strings ----
export const ctaPage1 = (profile: WaterProfile) =>
  profile === "C" ? "Ver qué puede filtrar AURAL" : "Ver mi solución";
export const nextLine1 = "Te mostramos cómo se frena, desde la primera ducha.";
export const advisoryC =
  "El arsénico afecta el agua que **bebés**, no la de tu ducha — la piel casi no lo absorbe. Para eso: análisis particular + filtro **NSF/ANSI 53** de consumo.";

export const buyCtaLine = (profile: WaterProfile) =>
  profile === "C" ? "Cuidá tu piel en la ducha." : PROFILES[profile].solTitle;
export const buyCtaLabel = (profile: WaterProfile) => (profile === "C" ? "Conocé AURAL" : "Elevá tu ducha");
export const trustLine = (profile: WaterProfile) =>
  profile === "C"
    ? "AURAL filtra cloro y dureza en la ducha · no el arsénico de consumo"
    : "Instalación 30 s · Garantía 90 días · Envío a todo el país";

export const BUY_HREF = "https://myaural.com/"; // https-only (matches ResultCtaSchema)
export const PRODUCT_LINE = "20 etapas de filtración en tu cabezal.";
export const BACK_LINK = "← Volver al diagnóstico";
export const CAUSES_HEADING = "Las 2 causas en tu agua y tus respuestas";
export const MATERIALS_HEADING = "Los materiales que filtran tu agua";
export const METER_LABEL = "Qué tan afectada está tu piel";
export const badge = (cp: string, name: string) => `CP ${cp} · ${name}`;
